import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../../shared/components/Button';
import { EmptyState } from '../../../shared/components/EmptyState';
import { PageContainer } from '../../../shared/components/PageContainer';
import { Textarea } from '../../../shared/components/Textarea';
import { useToast } from '../../../shared/components/Toast';
import { formatBrasilia } from '../../../shared/lib/date';
import { mensagemDeErro } from '../api';
import { CONFIDENTIALITY_LABELS, formatCep } from '../components/format';
import { PessoasModal } from '../components/PessoasModal';
import {
  useAprovarEndereco, useFilaDeValidacao, useRejeitarConta, useRejeitarEndereco, useValidarConta,
} from '../hooks/cadastro';

function Bloco({ title, count, children }: { title: string; count: number; children: React.ReactNode }) {
  return (
    <section className="space-y-2 rounded-lg border border-gray-200 bg-white p-4">
      <h2 className="text-base font-semibold text-gray-900">{title} <span className="text-sm font-normal text-gray-500">({count})</span></h2>
      {count === 0 ? <EmptyState size="compact" title="Nada pendente" /> : children}
    </section>
  );
}

/** Fila de validação do DP (design D15): cada bloco só para quem valida aquele tipo. */
export default function ValidacoesPage() {
  const toast = useToast();
  const fila = useFilaDeValidacao();
  const aprovarEndereco = useAprovarEndereco();
  const rejeitarEndereco = useRejeitarEndereco();
  const validarConta = useValidarConta();
  const rejeitarConta = useRejeitarConta();
  const [rejecting, setRejecting] = useState<{ kind: 'endereco' | 'conta'; id: string } | null>(null);
  const [validating, setValidating] = useState<{ id: string; divergent: boolean } | null>(null);
  const [text, setText] = useState('');

  async function run(action: () => Promise<unknown>, ok: string) {
    try {
      await action();
      toast.success(ok);
    } catch (err) {
      toast.error(mensagemDeErro(err, 'Não foi possível concluir a operação.'));
    }
  }

  const q = fila.data;

  return (
    <PageContainer
      title="Validações pendentes"
      subtitle="Documentos, endereços e contas bancárias aguardando o Departamento Pessoal."
      breadcrumbs={[{ label: 'Pessoas' }, { label: 'Validações' }]}
    >
      {fila.isLoading && <p className="text-sm text-gray-500">Carregando…</p>}
      <div className="space-y-5">
        {q?.documents && (
          <Bloco title="Documentos" count={q.documents.length}>
            <ul className="divide-y divide-gray-100 text-sm">
              {q.documents.map((d) => (
                <li key={d.id} className="flex items-center justify-between py-2">
                  <span>
                    <Link className="font-medium text-blue-700 hover:underline" to={`/pessoas/colaboradores/${d.userId}`}>{d.personName}</Link>
                    {' — '}{d.title} (v{d.version}) · {CONFIDENTIALITY_LABELS[d.confidentiality]} · {formatBrasilia(d.uploadedAt)}
                  </span>
                  <Link to={`/pessoas/colaboradores/${d.userId}`}><Button size="sm" variant="secondary">Analisar</Button></Link>
                </li>
              ))}
            </ul>
          </Bloco>
        )}
        {q?.addresses && (
          <Bloco title="Endereços enviados pelo portal" count={q.addresses.length}>
            <ul className="divide-y divide-gray-100 text-sm">
              {q.addresses.map((a) => (
                <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span>
                    <Link className="font-medium text-blue-700 hover:underline" to={`/pessoas/colaboradores/${a.userId}`}>{a.personName}</Link>
                    {' — '}{a.street}, {a.number} · {a.district} · {a.cityName}/{a.stateUf} · CEP {formatCep(a.zipCode)}
                  </span>
                  <span className="flex gap-2">
                    <Button size="sm" onClick={() => run(() => aprovarEndereco.mutateAsync(a.id), 'Endereço aprovado.')}>Aprovar</Button>
                    <Button size="sm" variant="ghost" onClick={() => { setText(''); setRejecting({ kind: 'endereco', id: a.id }); }}>Rejeitar</Button>
                  </span>
                </li>
              ))}
            </ul>
          </Bloco>
        )}
        {q?.bankAccounts && (
          <Bloco title="Contas bancárias" count={q.bankAccounts.length}>
            <ul className="divide-y divide-gray-100 text-sm">
              {q.bankAccounts.map((b) => (
                <li key={b.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span>
                    <Link className="font-medium text-blue-700 hover:underline" to={`/pessoas/colaboradores/${b.userId}`}>{b.personName}</Link>
                    {' — '}{b.bankCode} {b.bankName} · ag. {b.agency} · conta {b.account} · titular {b.holderName}
                    {b.holderDivergent && <span className="ml-1 text-red-600">(CPF diferente)</span>}
                  </span>
                  <span className="flex gap-2">
                    <Button size="sm" onClick={() => { setText(''); setValidating({ id: b.id, divergent: b.holderDivergent }); }}>Validar</Button>
                    <Button size="sm" variant="ghost" onClick={() => { setText(''); setRejecting({ kind: 'conta', id: b.id }); }}>Rejeitar</Button>
                  </span>
                </li>
              ))}
            </ul>
          </Bloco>
        )}
      </div>

      <PessoasModal
        open={rejecting !== null}
        title="Rejeitar"
        submitLabel="Rejeitar"
        submitVariant="danger"
        canSubmit={text.trim().length > 0}
        onClose={() => setRejecting(null)}
        onSubmit={() => rejecting && run(async () => {
          if (rejecting.kind === 'endereco') await rejeitarEndereco.mutateAsync({ id: rejecting.id, reason: text.trim() });
          else await rejeitarConta.mutateAsync({ id: rejecting.id, reason: text.trim() });
          setRejecting(null);
        }, 'Rejeição registrada.')}
      >
        <Textarea label="Motivo" value={text} onChange={(e) => setText(e.target.value)} />
      </PessoasModal>

      <PessoasModal
        open={validating !== null}
        title="Validar conta bancária"
        description={validating?.divergent ? 'O titular tem outro CPF: registre a justificativa da exceção.' : undefined}
        submitLabel="Validar"
        canSubmit={!validating?.divergent || text.trim().length > 0}
        onClose={() => setValidating(null)}
        onSubmit={() => validating && run(async () => {
          await validarConta.mutateAsync({ id: validating.id, note: text.trim() || undefined });
          setValidating(null);
        }, 'Conta validada.')}
      >
        <Textarea label="Observação / justificativa" value={text} onChange={(e) => setText(e.target.value)} />
      </PessoasModal>
    </PageContainer>
  );
}
